import React, { useState, useEffect } from 'react';
import { FaImage, FaTrash } from 'react-icons/fa';

const Step4Lighting = ({ state, callbacks }) => {
  const settings = state.randomizerSettings.light;
  const [availableHDRs, setAvailableHDRs] = useState([]);
  const [loadingHDRs, setLoadingHDRs] = useState(true);
  const hdrInputRef = React.useRef(null);

  // Lade verfügbare HDR-Dateien
  useEffect(() => {
    setLoadingHDRs(true);
    fetch(`${process.env.PUBLIC_URL}/hdri/index.json`)
      .then(res => res.json())
      .then(data => {
        const hdrs = Array.isArray(data) ? data : [];
        setAvailableHDRs(hdrs);
        setLoadingHDRs(false);
      })
      .catch(err => {
        console.warn('Could not load HDR index:', err);
        setAvailableHDRs([]);
        setLoadingHDRs(false);
      });
  }, []);

  const handleHDRUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const fileName = file.name.toLowerCase();
      const isHDR = fileName.endsWith('.hdr');
      const isEXR = fileName.endsWith('.exr');
      
      if (isHDR || isEXR) {
        const url = URL.createObjectURL(file);
        callbacks.setEnvironmentCustomHDR(url);
      } else {
        alert(`Ungültiger Dateityp: ${file.name}\nUnterstützte Formate: .hdr, .exr`);
      }
    }
  };

  const removeCustomHDR = () => {
    if (state.lighting.environment.customHDRUrl) {
      URL.revokeObjectURL(state.lighting.environment.customHDRUrl);
      callbacks.setEnvironmentCustomHDR(null);
    }
  };

  const selectHDRFromFolder = (hdrName) => {
    const url = `${process.env.PUBLIC_URL}/hdri/${hdrName}`;
    callbacks.setEnvironmentCustomHDR(url);
  };

  return (
    <div className="w-full bg-transparent p-0 space-y-4">
      {state.randomizers.lighting && (
        <>
          <div>
            <label className="block mb-2 text-xs font-medium text-dark-text-secondary tracking-tight">Umgebungslicht</label>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <label className="text-xs text-dark-text-muted tracking-tight font-medium">Farbe</label>
                <input
                  type="color"
                  value={state.lighting.ambient.color}
                  onChange={(e) => callbacks.setLighting({ 
                    ...state.lighting, 
                    ambient: { ...state.lighting.ambient, color: e.target.value }
                  })}
                  className="w-full h-12 cursor-pointer rounded-lg border border-dark-border transition-all duration-150 hover:border-dark-text-muted"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs text-dark-text-muted tracking-tight font-medium">Intensität</label>
                <input
                  type="number"
                  value={state.lighting.ambient.intensity}
                  onChange={(e) => callbacks.setLighting({ 
                    ...state.lighting, 
                    ambient: { ...state.lighting.ambient, intensity: parseFloat(e.target.value) || 0.5 }
                  })}
                  min="0"
                  max="2"
                  step="0.1"
                  className="w-full py-2.5 px-3 bg-white border border-dark-border rounded-lg text-sm text-dark-text transition-all duration-150 focus:outline-none focus:ring-1 focus:ring-dark-text focus:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed"
                />
              </div>
            </div>
          </div>

          <div>
            <label className="block mb-2 text-xs font-medium text-dark-text-secondary tracking-tight">Richtungslicht</label>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <label className="text-xs text-dark-text-muted tracking-tight font-medium">Farbe</label>
                <input
                  type="color"
                  value={typeof state.lighting.directional.color === 'string' 
                    ? state.lighting.directional.color 
                    : `#${((state.lighting.directional.color.r || 255).toString(16).padStart(2, '0'))}${((state.lighting.directional.color.g || 255).toString(16).padStart(2, '0'))}${((state.lighting.directional.color.b || 255).toString(16).padStart(2, '0'))}`}
                  onChange={(e) => {
                    const hex = e.target.value;
                    const r = parseInt(hex.slice(1, 3), 16);
                    const g = parseInt(hex.slice(3, 5), 16);
                    const b = parseInt(hex.slice(5, 7), 16);
                    callbacks.setLighting({ 
                      ...state.lighting, 
                      directional: { ...state.lighting.directional, color: { r, g, b } }
                    });
                  }}
                  className="w-full h-12 cursor-pointer rounded-lg border border-dark-border transition-all duration-150 hover:border-dark-text-muted"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs text-dark-text-muted tracking-tight font-medium">Intensität</label>
                <input
                  type="number"
                  value={state.lighting.directional.intensity}
                  onChange={(e) => callbacks.setLighting({ 
                    ...state.lighting, 
                    directional: { ...state.lighting.directional, intensity: parseFloat(e.target.value) || 1 }
                  })}
                  min="0"
                  max="5"
                  step="0.1"
                  className="w-full py-2.5 px-3 bg-white border border-dark-border rounded-lg text-sm text-dark-text transition-all duration-150 focus:outline-none focus:ring-1 focus:ring-dark-text focus:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed"
                />
              </div>
            </div>
          </div>

          <div>
            <label className="block mb-2 text-xs font-medium text-dark-text-secondary tracking-tight">Richtungslicht Position</label>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <label className="text-xs text-dark-text-muted tracking-tight font-medium">X</label>
                <input
                  type="number"
                  value={state.lighting.directional.position.x}
                  onChange={(e) => callbacks.setLighting({ 
                    ...state.lighting, 
                    directional: { 
                      ...state.lighting.directional, 
                      position: { ...state.lighting.directional.position, x: parseFloat(e.target.value) || 5 }
                    }
                  })}
                  step="0.1"
                  className="w-full py-2.5 px-3 bg-white border border-dark-border rounded-lg text-sm text-dark-text transition-all duration-150 focus:outline-none focus:ring-1 focus:ring-dark-text focus:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs text-dark-text-muted tracking-tight font-medium">Y</label>
                <input
                  type="number"
                  value={state.lighting.directional.position.y}
                  onChange={(e) => callbacks.setLighting({ 
                    ...state.lighting, 
                    directional: { 
                      ...state.lighting.directional, 
                      position: { ...state.lighting.directional.position, y: parseFloat(e.target.value) || 5 }
                    }
                  })}
                  step="0.1"
                  className="w-full py-2.5 px-3 bg-white border border-dark-border rounded-lg text-sm text-dark-text transition-all duration-150 focus:outline-none focus:ring-1 focus:ring-dark-text focus:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs text-dark-text-muted tracking-tight font-medium">Z</label>
                <input
                  type="number"
                  value={state.lighting.directional.position.z}
                  onChange={(e) => callbacks.setLighting({ 
                    ...state.lighting, 
                    directional: { 
                      ...state.lighting.directional, 
                      position: { ...state.lighting.directional.position, z: parseFloat(e.target.value) || 5 }
                    }
                  })}
                  step="0.1"
                  className="w-full py-2.5 px-3 bg-white border border-dark-border rounded-lg text-sm text-dark-text transition-all duration-150 focus:outline-none focus:ring-1 focus:ring-dark-text focus:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed"
                />
              </div>
            </div>
          </div>

          <div>
            <label className="block mb-2 text-xs font-medium text-dark-text-secondary tracking-tight">Eigene HDR-Datei</label>
            {state.lighting.environment.customHDRUrl ? (
              <div className="flex items-center gap-2 p-3 bg-white border border-dark-border rounded-lg">
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-dark-text font-medium truncate">
                    {state.lighting.environment.customHDRUrl.includes('blob:') 
                      ? 'Hochgeladene HDR-Datei' 
                      : state.lighting.environment.customHDRUrl.split('/').pop()}
                  </p>
                </div>
                <button
                  onClick={removeCustomHDR}
                  className="w-7 h-7 flex-shrink-0 rounded-lg border border-dark-border bg-white text-dark-text-muted hover:bg-dark-hover hover:text-dark-text transition-all flex items-center justify-center"
                  title="Entfernen"
                >
                  <FaTrash className="text-[10px]" />
                </button>
              </div>
            ) : (
              <>
                <div 
                  className="border-2 border-dashed border-dark-border rounded-lg p-4 cursor-pointer hover:border-dark-text-muted hover:bg-dark-hover transition-all flex flex-col items-center justify-center gap-2 min-h-[100px]"
                  onClick={() => hdrInputRef.current?.click()}
                >
                  <FaImage className="text-xl text-dark-text-muted" />
                  <span className="text-xs text-dark-text font-medium">HDR/EXR-Datei hochladen</span>
                  <span className="text-[10px] text-dark-text-muted">oder aus Ordner wählen</span>
                </div>
                <input
                  ref={hdrInputRef}
                  type="file"
                  accept=".hdr,.exr"
                  onChange={handleHDRUpload}
                  className="hidden"
                />
                
                {!loadingHDRs && availableHDRs.length > 0 && (
                  <div className="mt-3">
                    <label className="block mb-2 text-[10px] font-medium text-dark-text-muted tracking-tight">Verfügbare HDR-Dateien</label>
                    <div className="grid grid-cols-2 gap-2">
                      {availableHDRs.map((hdrName) => (
                        <button
                          key={hdrName}
                          onClick={() => selectHDRFromFolder(hdrName)}
                          className="py-2 px-3 text-xs bg-white border border-dark-border rounded-lg text-dark-text-secondary hover:bg-dark-hover hover:text-dark-text transition-all text-left truncate"
                          title={hdrName}
                        >
                          {hdrName}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>


          <div>
            <label className="block mb-2 text-xs font-medium text-dark-text-secondary tracking-tight">Environment Blur</label>
            <div className="flex items-center gap-3">
              <input
                type="range"
                value={state.lighting.environment.blur}
                onChange={(e) => callbacks.setLighting({ 
                  ...state.lighting, 
                  environment: { ...state.lighting.environment, blur: parseFloat(e.target.value) || 1.0 }
                })}
                min="0"
                max="1"
                step="0.01"
                className="flex-1 h-2 bg-dark-border rounded-lg appearance-none cursor-pointer accent-dark-text"
              />
              <input
                type="number"
                value={state.lighting.environment.blur.toFixed(2)}
                onChange={(e) => callbacks.setLighting({ 
                  ...state.lighting, 
                  environment: { ...state.lighting.environment, blur: Math.max(0, Math.min(1, parseFloat(e.target.value) || 1.0)) }
                })}
                min="0"
                max="1"
                step="0.01"
                className="w-20 py-2.5 px-3 bg-white border border-dark-border rounded-lg text-sm text-dark-text transition-all duration-150 focus:outline-none focus:ring-1 focus:ring-dark-text focus:ring-offset-1"
              />
            </div>
            <p className="text-[10px] text-dark-text-muted mt-1.5">
              {state.lighting.environment.blur === 0 
                ? 'Keine Unschärfe - scharfe Umgebung'
                : state.lighting.environment.blur === 1
                ? 'Maximale Unschärfe - sehr weiche Umgebung'
                : `Unschärfe: ${Math.round(state.lighting.environment.blur * 100)}%`}
            </p>
          </div>

          {/* Randomizer Settings */}
          {state.randomizers.light && (
            <>
              <div className="pt-4 border-t border-dark-border">
                <label className="block mb-3 text-xs font-medium text-dark-text-secondary tracking-tight">Randomisierung</label>
                
                <div>
                  <label className="block mb-2 text-xs font-medium text-dark-text-secondary tracking-tight">Intensitätsbereich</label>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <input
                        type="number"
                        value={settings.intensityRange.min}
                        onChange={(e) => callbacks.setRandomizerSettings({
                          ...state.randomizerSettings,
                          light: {
                            ...settings,
                            intensityRange: { ...settings.intensityRange, min: parseFloat(e.target.value) || 0 }
                          }
                        })}
                        step="0.1"
                        className="w-full py-2.5 px-3 bg-white border border-dark-border rounded-lg text-sm text-dark-text transition-all duration-150 focus:outline-none focus:ring-1 focus:ring-dark-text focus:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed"
                      />
                      <span className="text-xs text-dark-text-muted tracking-tight font-medium">Min</span>
                    </div>
                    <div className="space-y-1.5">
                      <input
                        type="number"
                        value={settings.intensityRange.max}
                        onChange={(e) => callbacks.setRandomizerSettings({
                          ...state.randomizerSettings,
                          light: {
                            ...settings,
                            intensityRange: { ...settings.intensityRange, max: parseFloat(e.target.value) || 0 }
                          }
                        })}
                        step="0.1"
                        className="w-full py-2.5 px-3 bg-white border border-dark-border rounded-lg text-sm text-dark-text transition-all duration-150 focus:outline-none focus:ring-1 focus:ring-dark-text focus:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed"
                      />
                      <span className="text-xs text-dark-text-muted tracking-tight font-medium">Max</span>
                    </div>
                  </div>
                </div>

                <div className="mt-4">
                  <label className="block mb-2 text-xs font-medium text-dark-text-secondary tracking-tight">Position X-Bereich</label>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <input
                        type="number"
                        value={settings.positionRange.minX}
                        onChange={(e) => callbacks.setRandomizerSettings({
                          ...state.randomizerSettings,
                          light: {
                            ...settings,
                            positionRange: { ...settings.positionRange, minX: parseFloat(e.target.value) || 0 }
                          }
                        })}
                        step="1"
                        className="w-full py-2.5 px-3 bg-white border border-dark-border rounded-lg text-sm text-dark-text transition-all duration-150 focus:outline-none focus:ring-1 focus:ring-dark-text focus:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed"
                      />
                      <span className="text-xs text-dark-text-muted tracking-tight font-medium">Min</span>
                    </div>
                    <div className="space-y-1.5">
                      <input
                        type="number"
                        value={settings.positionRange.maxX}
                        onChange={(e) => callbacks.setRandomizerSettings({
                          ...state.randomizerSettings,
                          light: {
                            ...settings,
                            positionRange: { ...settings.positionRange, maxX: parseFloat(e.target.value) || 0 }
                          }
                        })}
                        step="1"
                        className="w-full py-2.5 px-3 bg-white border border-dark-border rounded-lg text-sm text-dark-text transition-all duration-150 focus:outline-none focus:ring-1 focus:ring-dark-text focus:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed"
                      />
                      <span className="text-xs text-dark-text-muted tracking-tight font-medium">Max</span>
                    </div>
                  </div>
                </div>

                <div className="mt-4">
                  <label className="block mb-2 text-xs font-medium text-dark-text-secondary tracking-tight">Position Y-Bereich</label>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <input
                        type="number"
                        value={settings.positionRange.minY}
                        onChange={(e) => callbacks.setRandomizerSettings({
                          ...state.randomizerSettings,
                          light: {
                            ...settings,
                            positionRange: { ...settings.positionRange, minY: parseFloat(e.target.value) || 0 }
                          }
                        })}
                        step="1"
                        className="w-full py-2.5 px-3 bg-white border border-dark-border rounded-lg text-sm text-dark-text transition-all duration-150 focus:outline-none focus:ring-1 focus:ring-dark-text focus:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed"
                      />
                      <span className="text-xs text-dark-text-muted tracking-tight font-medium">Min</span>
                    </div>
                    <div className="space-y-1.5">
                      <input
                        type="number"
                        value={settings.positionRange.maxY}
                        onChange={(e) => callbacks.setRandomizerSettings({
                          ...state.randomizerSettings,
                          light: {
                            ...settings,
                            positionRange: { ...settings.positionRange, maxY: parseFloat(e.target.value) || 0 }
                          }
                        })}
                        step="1"
                        className="w-full py-2.5 px-3 bg-white border border-dark-border rounded-lg text-sm text-dark-text transition-all duration-150 focus:outline-none focus:ring-1 focus:ring-dark-text focus:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed"
                      />
                      <span className="text-xs text-dark-text-muted tracking-tight font-medium">Max</span>
                    </div>
                  </div>
                </div>

                <div className="mt-4">
                  <label className="block mb-2 text-xs font-medium text-dark-text-secondary tracking-tight">Position Z-Bereich</label>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <input
                        type="number"
                        value={settings.positionRange.minZ}
                        onChange={(e) => callbacks.setRandomizerSettings({
                          ...state.randomizerSettings,
                          light: {
                            ...settings,
                            positionRange: { ...settings.positionRange, minZ: parseFloat(e.target.value) || 0 }
                          }
                        })}
                        step="1"
                        className="w-full py-2.5 px-3 bg-white border border-dark-border rounded-lg text-sm text-dark-text transition-all duration-150 focus:outline-none focus:ring-1 focus:ring-dark-text focus:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed"
                      />
                      <span className="text-xs text-dark-text-muted tracking-tight font-medium">Min</span>
                    </div>
                    <div className="space-y-1.5">
                      <input
                        type="number"
                        value={settings.positionRange.maxZ}
                        onChange={(e) => callbacks.setRandomizerSettings({
                          ...state.randomizerSettings,
                          light: {
                            ...settings,
                            positionRange: { ...settings.positionRange, maxZ: parseFloat(e.target.value) || 0 }
                          }
                        })}
                        step="1"
                        className="w-full py-2.5 px-3 bg-white border border-dark-border rounded-lg text-sm text-dark-text transition-all duration-150 focus:outline-none focus:ring-1 focus:ring-dark-text focus:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed"
                      />
                      <span className="text-xs text-dark-text-muted tracking-tight font-medium">Max</span>
                    </div>
                  </div>
                </div>

                <div className="mt-4">
                  <label className="block mb-2 text-xs font-medium text-dark-text-secondary tracking-tight">Farbe RGB-Bereich</label>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <input
                        type="number"
                        value={settings.colorRange.min}
                        onChange={(e) => callbacks.setRandomizerSettings({
                          ...state.randomizerSettings,
                          light: {
                            ...settings,
                            colorRange: { ...settings.colorRange, min: parseInt(e.target.value) || 0 }
                          }
                        })}
                        min="0"
                        max="255"
                        step="1"
                        className="w-full py-2.5 px-3 bg-white border border-dark-border rounded-lg text-sm text-dark-text transition-all duration-150 focus:outline-none focus:ring-1 focus:ring-dark-text focus:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed"
                      />
                      <span className="text-xs text-dark-text-muted tracking-tight font-medium">Min (0-255)</span>
                    </div>
                    <div className="space-y-1.5">
                      <input
                        type="number"
                        value={settings.colorRange.max}
                        onChange={(e) => callbacks.setRandomizerSettings({
                          ...state.randomizerSettings,
                          light: {
                            ...settings,
                            colorRange: { ...settings.colorRange, max: parseInt(e.target.value) || 255 }
                          }
                        })}
                        min="0"
                        max="255"
                        step="1"
                        className="w-full py-2.5 px-3 bg-white border border-dark-border rounded-lg text-sm text-dark-text transition-all duration-150 focus:outline-none focus:ring-1 focus:ring-dark-text focus:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed"
                      />
                      <span className="text-xs text-dark-text-muted tracking-tight font-medium">Max (0-255)</span>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default Step4Lighting;
